// Visites de maintenance (option « contrats_maintenance ») : liste filtrable, visites en retard, accès à la fiche de visite.
// Le technicien n'y voit que les visites qui lui sont affectées ; aucun prix n'apparaît ici.
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { LIBELLES_MODE_VISITE, LIBELLES_STATUT_VISITE, STATUTS_VISITE } from '@shared/contrats/contrats.ts';
import { api } from '../../lib/api.ts';
import { date, pluriel } from '../../lib/format.ts';
import { Badge, Chargement, ChampChoix, EntetePage, EtatErreur, EtatVide, Panneau } from '../../components/ui.tsx';
import { OngletsInterventions } from '../interventions/chantiers/commun.tsx';
import { BadgeStatutVisite, CRENEAUX_LIBELLES, GardeVisites } from './commun.tsx';
import type { ListeVisites } from './types.ts';

export function PageVisites() {
  return <GardeVisites><ListeDesVisites /></GardeVisites>;
}

function ListeDesVisites() {
  const [params, setParams] = useSearchParams();
  const statut = params.get('statut') ?? '';
  const retard = params.get('retard') === '1';
  const site = params.get('site') ?? '';
  const changer = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const url = `/api/visites?${new URLSearchParams({ ...(statut ? { statut } : {}), ...(retard ? { retard: '1' } : {}) })}`;
  const q = useQuery({ queryKey: ['visites', url], queryFn: () => api.get<ListeVisites>(url) });
  const sites = useMemo(() => [...new Map((q.data?.visites ?? []).map((v) => [v.site_id, v.site])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], 'fr')), [q.data]);
  const visites = (q.data?.visites ?? []).filter((v) => !site || v.site_id === site);
  const filtre = !!(statut || retard || site);

  return (
    <div className="pile-l">
      <EntetePage titre="Visites de maintenance" sousTitre="Visites préventives prévues par les contrats : fenêtre de passage, personnes affectées, avancement des contrôles." />
      <OngletsInterventions />
      <div className="int-filtres cm-filtres" role="search" aria-label="Filtrer les visites">
        <ChampChoix libelle="Statut" value={statut} onChange={(e) => changer('statut', e.target.value)} vide="En cours et à venir"
          options={[...STATUTS_VISITE.map((s) => ({ valeur: s, libelle: LIBELLES_STATUT_VISITE[s] })), { valeur: 'toutes', libelle: 'Toutes, annulées comprises' }]} />
        <ChampChoix libelle="Site" value={site} onChange={(e) => changer('site', e.target.value)} vide="Tous les sites"
          options={sites.map(([id, nom]) => ({ valeur: id, libelle: nom }))} />
        <label className="case cm-case-filtre">
          <input type="checkbox" checked={retard} onChange={(e) => changer('retard', e.target.checked ? '1' : '')} />
          <span className="petit">En retard seulement</span>
        </label>
      </div>
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && (
        <Panneau serre titre={pluriel(visites.length, 'visite', 'visites')}>
          {visites.length === 0
            ? <EtatVide icone={<CalendarClock />} titre={filtre ? 'Aucune visite ne correspond' : 'Aucune visite prévue'}
                texte={filtre ? 'Modifiez les filtres.' : 'Les visites apparaissent ici dès qu’un contrat de maintenance est signé.'} />
            : (
              <div className="liste">
                {visites.map((v) => (
                  <Link key={v.id} to={`/interventions/visites/${v.id}`} className="liste-element">
                    <span className="liste-element-corps">
                      <span className="liste-element-titre">{LIBELLES_MODE_VISITE[v.mode]} n° {v.rang} — {v.site}</span>
                      <span className="petit discret">
                        <span className="mono">{v.reference}</span> · prévue le {date(v.date_due)} (entre le {date(v.fenetre_debut)} et le {date(v.fenetre_fin)})
                        {v.contrat_reference && <> · contrat <span className="mono">{v.contrat_reference}</span></>}
                      </span>
                      {v.affectations.length > 0 && (
                        <span className="petit">{v.affectations.map((a) => `${a.nom}, ${date(a.jour)} (${CRENEAUX_LIBELLES[a.creneau].toLowerCase()})`).join(' · ')}</span>
                      )}
                      <span className="int-ligne-badges">
                        <BadgeStatutVisite statut={v.statut} enRetard={v.en_retard} />
                        {v.affectations.length === 0 && v.statut !== 'realisee' && v.statut !== 'annulee' && <Badge ton="ambre">Personne affectée</Badge>}
                        {v.demo && <Badge ton="neutre">Démo</Badge>}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
        </Panneau>
      )}
    </div>
  );
}
